import { useEffect, useState } from "react";
import { useGetMetadocMetadataQuery } from "services/metadoc-api";

import { toEditableMedicationValues } from "../../utils/codeMetadataUtils";

import type { EditableMedicationValue } from "../../utils/codeMetadataUtils";

type UseMedicationMetadataArgs = {
  metadocId: number;
  activeDocumentGroupId?: number;
  shouldFetch: boolean;
};

type UseMedicationMetadataReturn = {
  medications: EditableMedicationValue[];
  isLoading: boolean;
  isFetching: boolean;
};

/*
encapsulates medication metadata fetching and ui mapping
keeps medication cards scoped to the active document group
*/
export const useMedicationMetadata = ({
  metadocId,
  activeDocumentGroupId,
  shouldFetch,
}: UseMedicationMetadataArgs): UseMedicationMetadataReturn => {
  const {
    data: medicationMetadata,
    isFetching,
    isLoading,
  } = useGetMetadocMetadataQuery(
    { metadocId, type: "medication" },
    { skip: !shouldFetch },
  );

  const [medications, setMedications] = useState<EditableMedicationValue[]>([]);

  useEffect(() => {
    setMedications([]);
  }, [metadocId]);

  useEffect(() => {
    if (!medicationMetadata || typeof activeDocumentGroupId !== "number") {
      setMedications([]);
      return;
    }

    const filteredMedicationMetadata = medicationMetadata.filter(
      (metadataRow) => metadataRow.document_group === activeDocumentGroupId,
    );

    setMedications(toEditableMedicationValues(filteredMedicationMetadata));
  }, [activeDocumentGroupId, medicationMetadata]);

  return {
    medications,
    isLoading,
    isFetching,
  };
};
